import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    SummaryPlayEvent,
    SummaryPlayEventsWrapper,
    OutsWrapper,
    IconWrapper,
    SummaryPlayEventDescription,
    Divider,
    EventNodeWrapper
} from './SummaryPlayEvents.style';
import { useSelector } from 'react-redux';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import PlayVideo, { videoShape } from './playResults/PlayVideo';
import { buildPlayerHeadshot, buildHomeRunDisplay } from './PlayResults';
import { generatePlayScoresInfo } from './PlayScores';

import Icon from '@mlb/react-patterns/v4/components/Icon/icon';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';
import buildAtBatOverlay from '@gameday/utils/buildAtBatOverlay';
import WinProbability from './playResults/WinProbability';
import getSummaryPlayIconName from './utils/getSummaryPlayIconName';
import Situation from './Situation';
import moundVisitHelper from '@gameday/utils/moundVisitHelper';
import { shouldDisplayTitle } from '@gameday/components/Play/play/utils/displayHelpers';
import { getContextData } from '@gameday/utils/selectors/getContextData';
import buildPlayerUrl from '@gameday/utils/buildPlayerUrl';

const buildImage = (isLoading, playerId, language, deviceInteraction, eventIcon, headshotType, disablePlayerLinks, playerPageContext) => {
    /*  Need to do these all up front, player id or not so
        we don't violate hook rules.  TODO: Move to separate component?
    */
    const url = buildPlayerUrl({ nameSlug: playerId, language, playerPageContext });

    const handlePlayerPage = useCallback((e) => {
        if (deviceInteraction.playercard({ title: 'Player Card', url, pid: playerId })) {
            e.preventDefault();
        }
    }, [deviceInteraction, url, playerId]);

    if (playerId) {
        return buildPlayerHeadshot(undefined, isLoading, url, deviceInteraction, playerId, headshotType, disablePlayerLinks);
    } else {
        // Temporary. TODO: use new icons once design finalizes them.
        return <IconWrapper
            isLoading={ isLoading }
        >
            { !isLoading
                ? <Icon
                    name={ getSummaryPlayIconName(eventIcon) }
                    width='24'
                />
                : <Skeleton width='48px' height='48px' isCircle={true}/> }
        </IconWrapper>;
    };
};

export const SummaryPlayEventTypes = {
    SCORING: 'scoring',
    HIGHLIGHT: 'highlight'
};

const getOuts = (outs, translate) => {
    let outKey = '';
    switch (outs) {
        case 1:
            outKey = 'game_oneout';
            break;
        case 2:
            outKey = 'game_twoouts';
            break;
        case 3:
            outKey = 'game_threeouts';
            break;
    }
    const outText = translate(outKey, `${outs} outs`);

    return outs ? outText : '';
};

const SummaryInfoComp = ({ isFinal, homeScore, awayScore, homeTeam, awayTeam, isTopInning, isScoringPlay }) => {
    const { InfoComp } = generatePlayScoresInfo({
        isFinal,
        homeScore,
        awayScore,
        homeTeam,
        awayTeam,
        isTopInning,
        isScoringPlay
    });

    return InfoComp;
};

const SummaryPlayEvents = ({
    isResult,
    playerId,
    teams = {},
    event,
    description,
    outs,
    homeScore,
    awayScore,
    isScoringPlay,
    resultVideo = {},
    eventType,
    isTopInning,
    isLoading,
    language = 'en',
    deviceInteraction = {},
    hideIcon = false,
    openAtBatOverlay,
    atBatIndex,
    showWinProb,
    keymoments,
    halfInning,
    showHitData,
    hitData,
    eventIcon,
    showSituationInfo,
    styleType = 'pill',
    playerContext
}) => {
    const translate = useTranslate();
    let showTitle = true;
    const awayTeam = teams.away?.team || {};
    const homeTeam = teams.home?.team || {};
    const { preCount, playEvents } = keymoments || {};
    const offense = playEvents && playEvents[playEvents?.length - 1]?.offense;
    const outText = getOuts(outs, translate);
    const getPlayerContextData = useSelector(getContextData);
    const { headshotType, disablePlayerLinks, playerPageContext } = getPlayerContextData(playerId);

    const {
        templateUrl,
        blurb,
        duration,
        slug,
        title,
        playbacks
    } = resultVideo;

    const image = buildImage(isLoading, playerId, language, deviceInteraction, eventIcon, headshotType, disablePlayerLinks, playerPageContext);

    const translatedHomeRunsTitle = {
        distanceLabel: translate('label_distance', 'Distance'),
        exitVelocityLabel: translate('label_exit_velocity', 'Exit Velocity'),
        launchAngleLabel: translate('label_launch_angle', 'Launch Angle')
    };

    const showVideo = !isLoading && templateUrl;
    const descriptionNode = isLoading
        ? null
        : (<>
            { description ? description + ' ' : '' }
            <OutsWrapper> {outText} </OutsWrapper>
        </>);
    const eventNode = isLoading
        ? null
        : (<>
            <EventNodeWrapper $isResult={isResult}> {event} </EventNodeWrapper>
        </>);

    // determine title display
    if (event) {
        if (event === description || event === description?.slice(0, -1)) { // if event and description are the same, or if description has trailing '.'
            showTitle = false;
        } else if (!shouldDisplayTitle(eventType)) { // if eventType is known to hide description
            showTitle = false;
        } else if (eventType === 'game_advisory') { // same as above with backwards compat for mound visits
            showTitle = !moundVisitHelper(eventType, description, event);
        }
    } else {
        showTitle = false;
    }

    return (
        <SummaryPlayEventsWrapper
            eventType={ eventType }
        >
            {hideIcon ? null : image}
            {!isLoading && showSituationInfo ? <Situation count = {preCount} offense={offense}/> : null}
            {showTitle && <SummaryPlayEvent
                isResult={ isResult }
                data-atbat-index={atBatIndex}
            >

                {isLoading ? <Skeleton height='18px' width='100px' /> : buildAtBatOverlay(playerId, atBatIndex, styleType === 'pill' ? eventNode : event, openAtBatOverlay)}
            </SummaryPlayEvent>}

            <SummaryPlayEventDescription
                verticallyCenter={ !showTitle }
                data-atbat-index={atBatIndex}
                needsMargin={ (showWinProb && keymoments) || (!isLoading && isScoringPlay)}
            >
                { isLoading ? <Skeleton height='22px' width='200px' /> : buildAtBatOverlay(playerId, atBatIndex, descriptionNode, openAtBatOverlay)}
            </SummaryPlayEventDescription>

            { (!isLoading && (isScoringPlay || keymoments?.atBatIsKeyMoment))
                ? <SummaryInfoComp
                    isFinal={false}
                    homeScore={homeScore}
                    awayScore={awayScore}
                    homeTeam={homeTeam}
                    awayTeam={awayTeam}
                    isTopInning={isTopInning}
                    isScoringPlay={isScoringPlay}
                />
                : null
            }

            { showWinProb && keymoments
                ? <WinProbability
                    keymoments={keymoments}
                    decimalSpot={1}
                    halfInning={ halfInning }
                    homeTeamName={homeTeam.abbreviation}
                    awayTeamName={awayTeam.abbreviation}
                />
                : null }

            { showVideo && <PlayVideo
                templateUrl={ templateUrl }
                blurb={ blurb }
                duration={ duration }
                id={slug}
                title={title}
                slug={slug}
                playbacks={playbacks}
                playerContext={playerContext}
            />
            }

            {
                !showVideo && showHitData && <Divider />
            }

            { showHitData
                ? buildHomeRunDisplay(
                    hitData,
                    translatedHomeRunsTitle
                )
                : null }
        </SummaryPlayEventsWrapper>
    );
};

SummaryPlayEvents.propTypes = {
    isResult: PropTypes.bool,
    playerId: PropTypes.number,
    teams: PropTypes.object,
    event: PropTypes.string,
    description: PropTypes.string,
    outs: PropTypes.number,
    homeScore: PropTypes.number,
    awayScore: PropTypes.number,
    isScoringPlay: PropTypes.bool,
    resultVideo: PropTypes.shape(videoShape),
    eventType: PropTypes.string,
    isTopInning: PropTypes.bool,
    isLoading: PropTypes.bool,
    language: PropTypes.string,
    deviceInteraction: PropTypes.object,
    openAtBatOverlay: PropTypes.func,
    atBatIndex: PropTypes.number,
    keymoments: PropTypes.object,
    showWinProb: PropTypes.bool,
    halfInning: PropTypes.string,
    eventIcon: PropTypes.string,
    showSituationInfo: PropTypes.bool,
    playerContext: PropTypes.string
};

export default SummaryPlayEvents;
